import React, { useState, useEffect, useRef } from 'react';
import './App.css';

// Import Academic Calendar Module
import AcademicCalendarModule from './components/Calendar/AcademicCalendarModule';


// Import Main Page Module
import MainPage from './components/Core/MainPage';

// Import Attendance Page Module
import AttendancePage from './components/Attendance/AttendancePage';

// Import School Event Page Module
import SchoolEventPage from './components/School/SchoolEventPage';

// Import Student Grades Page Module (using StudentInfo component for viewing)
import StudentGradesPage from './components/Grades/GradesManagementPage';

// Import Payment Records Module
import PaymentRecordsModule from './components/Payment/PaymentRecordsModule';

// Import News and Notification Module
import NewsAndNotificationModule from './components/NewsAndNotification/NewsAndNotificationModule';


// Import School Info Page
import SchoolInfoPage from './components/School/SchoolInfoPage';

// Import Supabase Auth
import SupabaseAuth from './components/Core/SupabaseAuth';

// Import Credits Panel
import CreditsPanel from './components/Core/CreditsPanel';

// Import Auth Sim
import AuthSim from './components/Core/AuthSim';

// Import ECA Page
import ECAPage from './components/ECA/ECAPage';

// Import new Information Pages
import StudentInformationDashboard from './components/StudentInfo/StudentInformationDashboard';
import ClassInfoPage from './components/ClassInfo';

// Import Group Messages Module
import GroupMessagesModule from './components/GroupMessages/GroupMessagesModule';

// Import Timetable Module (for viewing child's timetable)
import TimetableModule from './components/Timetable/TimetableModule';

// Import Homework Module
import HomeworkModule from './components/Homework/HomeworkModule';


// Import eNotice Module
import ENoticeModule from './components/eNotice';
// Import Library Module
import LibraryModule from './components/Library';

// Import Children Selector
import ChildrenSelector from './components/Common/ChildrenSelector';

// Import Supabase for fetching linked children
import { supabaseAdmin } from './lib/supabase';


// Import grades data
import { gradesData } from './Data/gradesData';

// Import mock data
import { mockAttendanceData } from './mockData/attendanceData';
import { mockPaymentData } from './mockData/paymentData';
import { mockNewsData } from './mockData/newsData';
import { mockSchoolEvents } from './mockData/schoolEvents';

// Import helper functions
import { calculateAttendanceRate, getAttendanceStatus } from './lib/attendanceHelpers';
import { formatCurrency, calculateTotalAmount } from './lib/paymentHelpers';
import { formatDate } from './lib/dateHelpers';

function App() {
  // Main application state
  const [activeTab, setActiveTab] = useState('main');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null); // Selected child for parent view
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Sidebar starts hidden by default
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const lastToggleTime = useRef(0); // Track last toggle time to prevent rapid clicking

  // Dummy state for demonstration
  const [attendanceData, setAttendanceData] = useState([]);
  const [paymentData, setPaymentData] = useState([]);
  const [newsData, setNewsData] = useState([]);
  const [schoolEvents, setSchoolEvents] = useState([]);

  // Initialize data on component mount
  useEffect(() => {
    // Simulate data loading
    setIsLoading(true);
    
    // Load mock data
    setAttendanceData(mockAttendanceData);
    setPaymentData(mockPaymentData);
    setNewsData(mockNewsData);
    setSchoolEvents(mockSchoolEvents);
    
    // Simulate authentication check
    setTimeout(() => {
      setIsAuthenticated(true);
      setCurrentUser({
        id: 'f6f781a2-dde8-489c-86f9-adbf1f498dde',
        name: 'Student A',
        role: 'student',
        class: 'S1A'
      });
      setIsLoading(false);
    }, 1000);

    // Handle window resize for responsive sidebar behavior
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      // Always hide sidebar on mobile, but don't auto-show on desktop
      if (mobile) {
        setIsSidebarOpen(false);
      }
      // Desktop: keep current sidebar state (don't auto-show)
    };

    // Set initial sidebar state based on screen size
    handleResize();

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Handle sidebar toggle with proper event handling
  const handleSidebarToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent rapid clicking by checking time between clicks
    const now = Date.now();
    if (now - lastToggleTime.current < 300) { // 300ms debounce
      return;
    }
    lastToggleTime.current = now;
    
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Handle tab changes
  const handleTabChange = (tabName, sourceInfo = null) => {
    console.log('App: Tab change requested:', tabName, 'with source:', sourceInfo);
    
    // Always hide sidebar after navigation (both mobile and desktop)
    setIsSidebarOpen(false);
    
    setActiveTab(tabName);
  };

  // Handle back navigation
  const handleBack = () => {
    // Always hide sidebar when navigating back (both mobile and desktop)
    setIsSidebarOpen(false);
    
    setActiveTab('main');
  };

  // Fetch linked children for parent users
  const fetchLinkedChildren = async (userId) => {
    try {
      console.log('[App] Fetching linked children for parent:', userId);
      
      // Step 1: Get parent profile from parent_profiles table (with tenant_id)
      const { data: parentProfile, error: profileError } = await supabaseAdmin
        .from('parent_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      if (profileError) {
        console.warn('[App] Error fetching parent profile:', profileError);
        // Fallback: Try to get children directly from saas_students using parent_id fields
        const { data: students, error: studentsError } = await supabaseAdmin
          .from('saas_students')
          .select('*')
          .or(`parent_id.eq.${userId},parent_1_id.eq.${userId},parent_2_id.eq.${userId}`)
          .eq('tenant_id', 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3') // Default tenant (school 1)
          .order('name');
        
        if (studentsError) {
          console.error('[App] Error fetching children from saas_students:', studentsError);
          return [];
        }
        
        return students || [];
      }
      
      if (!parentProfile) {
        console.warn('[App] No parent profile found for user:', userId);
        return [];
      }
      
      // Get tenant_id from parent profile (default to school 1 if not set)
      const tenantId = parentProfile.tenant_id || 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3';
      console.log('[App] Parent tenant_id:', tenantId);
      
      // Step 2: Get children from saas_students table
      // Try multiple parent_id fields that might link students to parents
      const { data: students, error: studentsError } = await supabaseAdmin
        .from('saas_students')
        .select('*')
        .or(`parent_id.eq.${userId},parent_1_id.eq.${userId},parent_2_id.eq.${userId},parent_id.eq.${parentProfile.id},parent_1_id.eq.${parentProfile.id},parent_2_id.eq.${parentProfile.id}`)
        .eq('tenant_id', tenantId)
        .order('name');
      
      if (studentsError) {
        console.error('[App] Error fetching children from saas_students:', studentsError);
        return [];
      }
      
      console.log('[App] Found children:', students?.length || 0);
      return students || [];
    } catch (err) {
      console.error('[App] Error in fetchLinkedChildren:', err);
      return [];
    }
  };

  // Update currentUser to include children when user is a parent
  useEffect(() => {
    if (currentUser?.role === 'parent' && currentUser?.id) {
      // Fetch linked children from database
      fetchLinkedChildren(currentUser.id).then(children => {
        console.log('[App] Fetched children:', children);
        if (children && children.length > 0) {
          setCurrentUser(prev => ({ ...prev, children }));
          // Set first child as selected by default
          setSelectedChild(children[0]);
        } else {
          // If no children found, set empty array
          setCurrentUser(prev => ({ ...prev, children: [] }));
          setSelectedChild(null);
        }
      });
    } else if (currentUser && currentUser.role !== 'parent') {
      // Clear selected child if user is not a parent
      setSelectedChild(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, currentUser?.role]);

  // Render main content based on active tab
  const renderMainContent = () => {
    switch (activeTab) {
      case 'main':
        return (
          <MainPage
            currentUser={currentUser}
            attendanceData={attendanceData}
            paymentData={paymentData}
            newsData={newsData}
            schoolEvents={schoolEvents}
            onTabChange={handleTabChange}
            calculateAttendanceRate={calculateAttendanceRate}
            getAttendanceStatus={getAttendanceStatus}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
          />
        );
      
      case 'attendance':
        return (
          <AttendancePage
            attendanceData={attendanceData}
            onBack={handleBack}
            onTabChange={handleTabChange}
            calculateAttendanceRate={calculateAttendanceRate}
            getAttendanceStatus={getAttendanceStatus}
            viewOnly={true}
            currentUser={currentUser}
          />
        );
      
      case 'school-event':
        return (
          <SchoolEventPage
            schoolEvents={schoolEvents}
            onBack={handleBack}
            onTabChange={handleTabChange}
            formatDate={formatDate}
          />
        );
      
      case 'grades':
        return (
          <StudentGradesPage
            gradesData={gradesData}
            onBack={handleBack}
            formatDate={formatDate}
            viewOnly={true}
            currentUser={currentUser}
          />
        );
      
      case 'payment':
        return (
          <PaymentRecordsModule
            paymentData={paymentData}
            onBack={handleBack}
            onTabChange={handleTabChange}
            formatCurrency={formatCurrency}
            calculateTotalAmount={calculateTotalAmount}
            formatDate={formatDate}
          />
        );
      
      case 'news':
        return (
          <NewsAndNotificationModule
            newsData={newsData}
            onBack={handleBack}
            formatDate={formatDate}
          />
        );
      
      case 'eca':
        return (
          <ECAPage 
            onTabChange={handleTabChange}
            currentUser={currentUser}
            role="parent"
          />
        );
      
      case 'academic-calendar':
        return (
          <AcademicCalendarModule
            onBack={handleBack}
          />
        );
      
      case 'school-info':
        return (
          <SchoolInfoPage
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'student-info':
        return (
          <StudentInformationDashboard
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'class-info':
        return (
          <ClassInfoPage
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'group-messages':
        return (
          <GroupMessagesModule currentUser={currentUser} />
        );
      
      // Timetable - kept for viewing child's timetable (can be accessed via other means)
      case 'timetable':
        return (
          <TimetableModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
            viewOnly={true}
            role="parent"
          />
        );
      
      case 'homework':
        return (
          <HomeworkModule
            currentUser={currentUser}
            selectedChild={selectedChild}
            onBack={handleBack}
            onTabChange={handleTabChange}
            viewOnly={true}
            role="parent"
          />
        );
      
      case 'e-notices':
                return (
                  <ENoticeModule
                    currentUser={currentUser}
                    onBack={handleBack}
                    onTabChange={handleTabChange}
                  />
                );
              case 'library':
                return (
                  <LibraryModule
                    currentUser={currentUser}
                    onBack={handleBack}
                    onTabChange={handleTabChange}
                    role="parent"
                  />
                );
              
      default:
        return (
          <MainPage
            currentUser={currentUser}
            attendanceData={attendanceData}
            paymentData={paymentData}
            newsData={newsData}
            schoolEvents={schoolEvents}
            onTabChange={handleTabChange}
            calculateAttendanceRate={calculateAttendanceRate}
            getAttendanceStatus={getAttendanceStatus}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
          />
        );
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading application...</p>
      </div>
    );
  }

  // Show authentication if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="auth-container">
        <SupabaseAuth
          onLogin={async (user) => {
            setIsAuthenticated(true);
            setCurrentUser(user);
            
            // Fetch children if user is a parent
            if (user?.role === 'parent' && user?.id) {
              const children = await fetchLinkedChildren(user.id);
              if (children && children.length > 0) {
                setCurrentUser(prev => ({ ...prev, children }));
                setSelectedChild(children[0]);
              }
            }
          }}
        />
        <AuthSim
          onLogin={async (user) => {
            setIsAuthenticated(true);
            setCurrentUser(user);
            
            // Fetch children if user is a parent
            if (user?.role === 'parent' && user?.id) {
              const children = await fetchLinkedChildren(user.id);
              if (children && children.length > 0) {
                setCurrentUser(prev => ({ ...prev, children }));
                setSelectedChild(children[0]);
              }
            }
          }}
        />
      </div>
    );
  }

  // Main application render
  return (
    <div className="App">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <button 
            className={`sidebar-toggle ${isSidebarOpen ? 'active' : ''}`}
            onClick={handleSidebarToggle}
            aria-label="Toggle sidebar"
            title={isSidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
          >
            {isSidebarOpen ? '✕' : '☰'}
          </button>
          <h1>MyClassParent</h1>
          <div className="user-info">
            <span>Welcome, {currentUser?.name}</span>
            {currentUser?.role === 'parent' && currentUser?.children && currentUser.children.length > 1 && (
              <ChildrenSelector
                children={currentUser.children}
                selectedChild={selectedChild}
                onChildChange={setSelectedChild}
              />
            )}
            <button 
              className="logout-btn"
              onClick={() => {
                setIsAuthenticated(false);
                setCurrentUser(null);
                setSelectedChild(null);
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Backdrop */}
      {isSidebarOpen && isMobile && (
        <div 
          className="sidebar-backdrop"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsSidebarOpen(false);
          }}
        />
      )}

      {/* Sidebar Navigation */}
      <div 
        className={`app-sidebar ${isSidebarOpen ? 'open' : 'closed'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <nav className="sidebar-navigation">
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'main' ? 'active' : ''}`}
              onClick={() => handleTabChange('main')}
            >
              🏠 主頁
            </button>
          </div>
          
          {/* Academic Progress */}
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'grades' ? 'active' : ''}`}
              onClick={() => handleTabChange('grades')}
            >
              📈 學生成績
            </button>
          </div>
          
          {/* Homework */}
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'homework' ? 'active' : ''}`}
              onClick={() => handleTabChange('homework')}
            >
              📚 家課
            </button>
          </div>
          
          {/* Attendance */}
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'attendance' ? 'active' : ''}`}
              onClick={() => handleTabChange('attendance')}
            >
              📊 考勤紀錄
            </button>
          </div>
          
          {/* Communication */}
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'group-messages' ? 'active' : ''}`}
              onClick={() => handleTabChange('group-messages')}
            >
              💬 小組訊息
            </button>
          </div>
          
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'e-notices' ? 'active' : ''}`}
              onClick={() => handleTabChange('e-notices')}
            >
              📋 電子通告
            </button>
          </div>
          
          {/* Calendar */}
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'academic-calendar' ? 'active' : ''}`}
              onClick={() => handleTabChange('academic-calendar')}
            >
              📅 校曆表
            </button>
          </div>
          
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'school-event' ? 'active' : ''}`}
              onClick={() => handleTabChange('school-event')}
            >
              🎉 學校活動
            </button>
          </div>
          
          {/* Library */}
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'library' ? 'active' : ''}`}
              onClick={() => handleTabChange('library')}
            >
              📚 圖書館
            </button>
          </div>
          
          {/* ECA */}
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'eca' ? 'active' : ''}`}
              onClick={() => handleTabChange('eca')}
            >
              🏃 課外活動
            </button>
          </div>
          
          {/* Information */}
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'student-info' ? 'active' : ''}`}
              onClick={() => handleTabChange('student-info')}
            >
              👨‍🎓 子女資訊
            </button>
          </div>
          
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'class-info' ? 'active' : ''}`}
              onClick={() => handleTabChange('class-info')}
            >
              🏫 班級資訊
            </button>
          </div>
          
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'school-info' ? 'active' : ''}`}
              onClick={() => handleTabChange('school-info')}
            >
              ℹ️ 學校資訊
            </button>
          </div>
          
          {/* News */}
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'news' ? 'active' : ''}`}
              onClick={() => handleTabChange('news')}
            >
              📰 學校宣佈
            </button>
          </div>
        </nav>
      </div>

      {/* Main Content Area */}
      <main className={`app-main ${isSidebarOpen ? 'with-sidebar' : 'without-sidebar'}`}>
        {renderMainContent()}
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <CreditsPanel />
      </footer>
    </div>
  );
}

export default App;
